// API client for dashboard endpoints
// Authentication via JWT (Supabase Auth)

import { getAccessToken } from './lib/supabase';
import type { Instance, QuotaResponse, SubscriptionResponse, BillingPackage } from './types';

// Use VITE_API_URL for Gateway, fallback to relative path for local dev
const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const API_BASE = `${API_BASE_URL}/v1/dash`;
const INSTANCES_BASE = `${API_BASE_URL}/v1/instances`;
const BILLING_BASE = `${API_BASE_URL}/v1/billing`;

export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

async function apiRequest<T>(
  path: string,
  options: globalThis.RequestInit = {}
): Promise<T> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers,
  } as globalThis.RequestInit);

  if (response.status === 401) {
    throw new AuthError('Unauthorized - please log in');
  }

  const data = (await response.json()) as T & { error?: string };

  if (!response.ok) {
    throw new Error(data.error || `API error: ${response.status}`);
  }

  return data;
}

/**
 * API request helper for /v1/instances endpoint
 */
async function apiRequestInstances<T>(
  path: string,
  options: globalThis.RequestInit = {}
): Promise<T> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${INSTANCES_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers,
  } as globalThis.RequestInit);

  if (response.status === 401) {
    throw new AuthError('Unauthorized - please log in');
  }

  const data = (await response.json()) as T & { error?: { message?: string } | string };

  if (!response.ok) {
    const errorMessage = typeof data.error === 'object' ? data.error.message : data.error;
    throw new Error(errorMessage || `API error: ${response.status}`);
  }

  return data;
}

// =====================
// Instance CRUD API
// =====================

export interface ListInstancesResponse {
  success: boolean;
  instances: Instance[];
}

export interface CreateInstanceResponse {
  success: boolean;
  instanceId: string;
  name: string;
  status?: string;  // Backend returns 'starting' after auto-triggering start
}

/**
 * List user's instances
 */
export async function listInstances(): Promise<Instance[]> {
  const response = await apiRequestInstances<ListInstancesResponse>('');
  return response.instances || [];
}

/**
 * Create a new instance
 * Backend auto-triggers start, so status will be 'starting' on success
 */
export async function createInstance(name: string = 'My Agent'): Promise<Instance> {
  const response = await apiRequestInstances<CreateInstanceResponse>('', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });

  // Use status from backend (should be 'starting' after auto-start)
  return {
    id: response.instanceId,
    name: response.name,
    status: (response.status as Instance['status']) || 'starting',
    createdAt: new Date().toISOString(),
  };
}

/**
 * Delete an instance
 */
export async function deleteInstance(instanceId: string): Promise<void> {
  await apiRequestInstances<{ success: boolean }>(`/${instanceId}`, {
    method: 'DELETE',
  });
}

/**
 * Start an instance
 */
export async function startInstance(instanceId: string): Promise<void> {
  await apiRequest<{ success: boolean }>('/start', {
    method: 'POST',
    body: JSON.stringify({ instanceId }),
  });
}

/**
 * Stop an instance
 */
export async function stopInstance(instanceId: string): Promise<void> {
  await apiRequest<{ success: boolean }>('/stop', {
    method: 'POST',
    body: JSON.stringify({ instanceId }),
  });
}

/**
 * Suspend an instance (scale-to-zero)
 */
export async function suspendInstance(instanceId: string): Promise<void> {
  await apiRequest<{ success: boolean }>('/suspend', {
    method: 'POST',
    body: JSON.stringify({ instanceId }),
  });
}

// =====================
// Health Status API
// =====================

export interface HealthStatusResponse {
  instanceId: string;
  status: 'stopped' | 'starting' | 'running' | 'suspended';
  processId?: string;
  connectionCount: number;
  disconnectedSessionCount: number;
  pendingMessageCount: number;
  hasActiveConnections: boolean;
  idleTimeoutMs: number;
  uptimeMs: number;
}

export async function getHealthStatus(instanceId: string): Promise<HealthStatusResponse> {
  const response = await apiRequest<{
    instanceId: string;
    status: string;
    machineId?: string;
    idleTimeoutMs: number;
    uptimeMs: number;
    lastActivityAt?: number;
    lastStartedAt?: number;
  }>('/health', {
    method: 'POST',
    body: JSON.stringify({ instanceId }),
  });

  // Map Core DO response to Dashboard format
  return {
    instanceId: response.instanceId,
    status: response.status as HealthStatusResponse['status'],
    processId: response.machineId,
    connectionCount: 0, // Not tracked by Core DO
    disconnectedSessionCount: 0,
    pendingMessageCount: 0,
    hasActiveConnections: false,
    idleTimeoutMs: response.idleTimeoutMs,
    uptimeMs: response.uptimeMs,
  };
}

// =====================
// Billing API
// =====================

/**
 * API request helper for /v1/billing endpoint
 */
async function apiRequestBilling<T>(
  path: string,
  options: globalThis.RequestInit = {}
): Promise<T> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${BILLING_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers,
  } as globalThis.RequestInit);

  if (response.status === 401) {
    throw new AuthError('Unauthorized - please log in');
  }

  // For 402 (quota exhausted), return the response body as-is
  const data = (await response.json()) as T & { error?: { code?: string; message?: string } | string };

  // Don't throw for 402 - let caller handle quota exhausted state
  if (!response.ok && response.status !== 402) {
    const errorMessage = typeof data.error === 'object' ? data.error.message : data.error;
    throw new Error(errorMessage || `API error: ${response.status}`);
  }

  return data;
}

/**
 * Get quota for an instance
 */
export async function getQuota(instanceId: string): Promise<QuotaResponse> {
  return apiRequestBilling<QuotaResponse>(`/quota?instanceId=${encodeURIComponent(instanceId)}`);
}

/**
 * Get subscription for an instance
 */
export async function getSubscription(instanceId: string): Promise<SubscriptionResponse> {
  return apiRequestBilling<SubscriptionResponse>(`/subscription?instanceId=${encodeURIComponent(instanceId)}`);
}

/**
 * Get available billing packages
 */
export async function getBillingPackages(): Promise<BillingPackage[]> {
  const response = await apiRequestBilling<{ success: boolean; packages: BillingPackage[] }>('/packages');
  return response.packages || [];
}

/**
 * Create Stripe checkout session
 */
export interface CheckoutResponse {
  success: boolean;
  checkoutUrl: string;
  sessionId: string;
}

export async function createCheckout(
  packageId: string,
  instanceName: string = 'My Agent'
): Promise<CheckoutResponse> {
  return apiRequestBilling<CheckoutResponse>('/checkout', {
    method: 'POST',
    body: JSON.stringify({ packageId, instanceName }),
  });
}

/**
 * Cancel subscription for an instance
 */
export interface CancelSubscriptionResponse {
  success: boolean;
  effectiveDate: string;
  message: string;
}

export async function cancelSubscription(instanceId: string): Promise<CancelSubscriptionResponse> {
  return apiRequestBilling<CancelSubscriptionResponse>('/subscription/cancel', {
    method: 'POST',
    body: JSON.stringify({ instanceId }),
  });
}
